import React, { useState, useEffect } from 'react';
import { usePlacementManager } from '../../../hooks/usePlacementManager';
import './ConfigTabsShared.css';

interface BlockedPagesTabProps {
    handleForm: any;
}

const BlockedPagesTab: React.FC<BlockedPagesTabProps> = ({ handleForm }) => {
    const { addBlockedPage, removeBlockedPage } = usePlacementManager(handleForm);
    const [collapsedPages, setCollapsedPages] = useState<{ [key: number]: boolean }>({});

    const handleAddPage = () => {
        const currentLength = (handleForm.values.blockedPages || []).length;
        // Set the new page to be open BEFORE adding it
        setCollapsedPages(prev => ({
            ...prev,
            [currentLength]: false // New page should be open
        }));
        addBlockedPage();
    };

    // Initialize all pages as collapsed when page loads (only on mount or when completely reset)
    useEffect(() => {
        const pages = handleForm.values.blockedPages || [];
        if (pages.length === 0) {
            setCollapsedPages({});
            return;
        }

        // Only set collapsed state for pages that don't have state yet
        setCollapsedPages(prev => {
            const newState = { ...prev };
            pages.forEach((_: any, index: any) => {
                if (!(index in newState)) {
                    newState[index] = true; // Default to collapsed for existing pages
                }
            });
            return newState;
        });
    }, [handleForm.values.blockedPages?.length]);

    const updatePageField = (index: number, field: string, value: any) => {
        const updatedPages = [...handleForm.values.blockedPages];
        updatedPages[index] = { ...updatedPages[index], [field]: value };
        handleForm.setFieldValue('blockedPages', updatedPages);
    };

    const togglePageEnabled = (index: number, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent header click
        const page = handleForm.values.blockedPages[index];
        const newEnabledState = !page.enabled;

        updatePageField(index, 'enabled', newEnabledState);

        setCollapsedPages(prev => ({
            ...prev,
            [index]: page.enabled // Collapse when disabled
        }));
    };

    const toggleCollapse = (index: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setCollapsedPages(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleHeaderClick = (index: number) => {
        setCollapsedPages(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const hasPages = (handleForm.values.blockedPages || []).length > 0;

    return (
        <div className="content-section">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="mb-1">Páginas Bloqueadas</h5>
                    <p className="text-muted small mb-0">
                        Configure páginas onde a tag não deve ser executada
                    </p>
                </div>
                {hasPages && (
                    <button
                        type="button"
                        className="btn-add-config"
                        onClick={handleAddPage}
                    >
                        ➕ Adicionar Página
                    </button>
                )}
            </div>

            {(handleForm.values.blockedPages || []).map((page: any, index: number) => {
                const isCollapsed = collapsedPages[index];
                return (
                    <div key={index} className={`config-card ${!page.enabled ? 'disabled' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
                        <div className="config-header" onClick={() => handleHeaderClick(index)}>
                            {/* Left: Collapse button */}
                            <div className="config-header-left">
                                <button
                                    type="button"
                                    className={`collapse-button ${isCollapsed ? 'collapsed' : ''}`}
                                    onClick={(e) => toggleCollapse(index, e)}
                                >
                                    ⌄
                                </button>
                                <div className="config-title">
                                    🚫 Página #{index + 1}
                                    {page.name && ` - ${page.name}`}
                                </div>
                            </div>

                            {/* Right: Switch */}
                            <div className="config-header-right">
                                <div className="custom-switch-container">
                                    <div
                                        className={`custom-switch ${page.enabled ? 'enabled' : ''}`}
                                        onClick={(e) => togglePageEnabled(index, e)}
                                    >
                                        <div className="custom-switch-handle" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="config-body">
                            <div className="form-section">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Nome</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={page.name || ''}
                                                    placeholder="Ex: Página de Login"
                                                    onChange={(e) => updatePageField(index, 'name', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Tipo</label>
                                                <select
                                                    className="form-select"
                                                    value={page.type || 'path'}
                                                    onChange={(e) => updatePageField(index, 'type', e.target.value)}
                                                >
                                                    <option value="path">Path</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Padrão (Regex)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={page.pattern || ''}
                                                    placeholder="Ex: /login, /admin/*"
                                                    onChange={(e) => updatePageField(index, 'pattern', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Page Controls */}
                        <div className="config-controls">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">
                                    Criado em: {new Date().toLocaleDateString()}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn-remove-config"
                                onClick={() => removeBlockedPage(index)}
                                title="Remover página"
                            >
                                🗑️ Remover
                            </button>
                        </div>
                    </div>
                );
            })}

            {(handleForm.values.blockedPages || []).length === 0 && (
                <div className="empty-config-container">
                    <div className="empty-config-icon">🚫</div>
                    <h4 className="empty-config-title">Nenhuma página bloqueada</h4>
                    <p className="empty-config-subtitle">
                        Configure páginas ou padrões de URL onde a tag não deve ser executada,
                        como páginas de login, admin ou checkout.
                    </p>
                    <button
                        type="button"
                        className="btn-add-config"
                        onClick={handleAddPage}
                    >
                        ➕ Criar Primeira Página Bloqueada
                    </button>
                </div>
            )}
        </div>
    );
};

export default BlockedPagesTab;